import * as React from "react";

import { EventCategory } from "../../../utils/analytics";
import { useClickOut } from "../../../hooks/use-click-out";
import Actionable from "../actionable/actionable";
import HeightTransition from "../height-transition/height-transition";
import { ChevronIcon } from "../icon-components/chevron-icon";
import { Colors } from "../../design-tokens/colors";
import styles from "./sorter.styles";

interface SorterProps {
  value: string;
  items: string[];
  onSelect: (value: string) => void;
  eventCategory: EventCategory;
}

const Sorter: React.FC<SorterProps> = ({
  value,
  items,
  onSelect,
  eventCategory,
}) => {
  const filterRef = React.useRef<HTMLDivElement>(null);

  const [isOpen, toggleOpen] = React.useState<boolean>(false);

  useClickOut({ ref: filterRef, callback: () => toggleOpen(false) });

  const handleSelectOption = (item: string) => {
    onSelect(item);
    toggleOpen(false);
  };

  return (
    <div css={styles.root} ref={filterRef}>
      <Actionable
        component="div"
        role="button"
        ariaLabel={value}
        css={styles.controlLabel}
        onClick={() => toggleOpen(!isOpen)}
        analytics={{
          category: eventCategory,
          action: `${isOpen ? "Close" : "Open"} - Sort by`,
          label: value,
        }}
      >
        <span>
          Sort by <strong css={styles.sortValue}>{value}</strong>
        </span>
        <span
          css={styles.controlIcon}
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <ChevronIcon
            direction="down"
            size={14}
            color={Colors.Blue}
            darkModeColor={Colors.DarkModeTextBlue}
          />
        </span>
      </Actionable>

      <HeightTransition
        isClosed={!isOpen}
        transitionTime={0.2}
        css={styles.dropdownBox}
      >
        {items.map((item) => (
          <Actionable
            key={item}
            component="div"
            role="option"
            ariaLabel={item}
            css={styles.listItem}
            onClick={() => handleSelectOption(item)}
            analytics={{
              category: eventCategory,
              action: `Select - Sort by ${item}`,
              label: item,
            }}
          >
            {value === item ? <strong>{item}</strong> : item}
          </Actionable>
        ))}
      </HeightTransition>
    </div>
  );
};

export default Sorter;
